import React, {
  CSSProperties,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import classNames from 'classnames'

interface IProps {
  lineHeight: number
  innerStyle?: CSSProperties
  style?: CSSProperties
}

const Collapse: React.FC<IProps> = ({
  children,
  lineHeight,
  innerStyle,
  style,
}) => {
  const [hasBtn, setHasBtn] = useState(false)
  const rootRef = useRef<HTMLDivElement | null>(null)
  const calcHasBtn = useCallback(() => {
    const { current } = rootRef

    const isXOverflow =
      (current?.scrollWidth || 0) > (current?.clientWidth || 0)
    const isYOverflow = (current?.clientHeight || 0) > lineHeight

    setHasBtn(isXOverflow || isYOverflow)
  }, [])

  const [isCollapsed, setIsCollapsed] = useState(true)

  const handleToggle = useCallback(() => {
    setIsCollapsed(!isCollapsed)
    try {
      rootRef.current!.scrollTo(0, 0)
    } catch (e) {
      // 兼容性降级，不需要监控
    }
  }, [isCollapsed])

  // 内容改变
  useEffect(() => {
    calcHasBtn()
  }, [children])

  // 屏幕改变
  useEffect(() => {
    window.addEventListener('resize', calcHasBtn)
    // 降级可用resize
    // const observer = new (window as any).ResizeObserver(calcHasBtn)
    // observer.observe(rootRef.current!)
    return () => {
      window.removeEventListener('resize', calcHasBtn)
      // observer.unobserve(rootRef.current!)
    }
  }, [calcHasBtn])

  return (
    <React.Fragment>
      <div className="collapse-wrapper">
        <div
          className={`collapse ${isCollapsed ? 'collapsed' : 'expanded'}`}
          style={style}
          ref={rootRef}
        >
          <div className="inner" style={innerStyle}>
            {children}
          </div>
        </div>
        {hasBtn ? (
          <div
            className={classNames({
              'btn-wrapper': true,
              'btn-wrapper__collapsed': isCollapsed,
              'btn-wrapper__expand': !isCollapsed,
            })}
          >
            <button
              className={classNames({
                btn: true,
                collapsed: isCollapsed,
              })}
              type="button"
              onClick={handleToggle}
            >
              {isCollapsed ? '展开' : '收起'}
            </button>
          </div>
        ) : null}
      </div>
      <style jsx>
        {`
          .collapse-wrapper {
            position: relative;
          }
          .collapse {
            overflow: hidden;
            position: relative;
          }
          .collapsed {
            max-height: ${lineHeight}px;
          }
          .expanded {
            max-height: 146px;
            overflow-y: scroll;
            margin-right: -5px;
            margin-bottom: ${lineHeight + 16}px;
          }
          .expanded::-webkit-scrollbar {
            width: 5px;
            background: transparent;
          }
          .expanded::-webkit-scrollbar-track {
            width: 5px;
            background: transparent;
          }
          .expanded:hover::-webkit-scrollbar-thumb {
            width: 5px;
            height: 30px;
            width: 5px;
            border-radius: 3.5px;
            background-color: #d8d8d8;
          }
          .inner {
            display: flex;
            flex-wrap: wrap;
          }
          .collapsed .inner {
            flex-wrap: nowrap;
          }
          .btn-wrapper {
            position: absolute;
            right: 0;
            bottom: 0;
            line-height: ${lineHeight}px;
            text-align: right;
            background: linear-gradient(
              to right,
              rgba(242, 244, 250, 0),
              #f2f4fa 50%,
              #f2f4fa
            );
          }
          .btn-wrapper__collapsed {
            width: 120px;
            background: linear-gradient(
              to right,
              rgba(242, 244, 250, 0),
              #f2f4fa 50%,
              #f2f4fa
            );
          }
          .btn-wrapper__expand {
            bottom: -${lineHeight + 4}px;
          }
          .btn {
            display: inline-block;
            position: relative;
            padding-right: 16px;
            width: 60px;
            text-align: right;
          }
          .btn:after {
            content: '';
            width: 0;
            height: 0;
            position: absolute;
            right: 4px;
            top: 50%;
            transform: translateY(-50%);
            border-width: 4px;
            border-top-width: 0;
            border-bottom-width: 6px;
            border-color: transparent;
            border-bottom-color: #606370;
          }
          .btn.collapsed:after {
            border-top-width: 6px;
            border-bottom-width: 0;
            border-top-color: #606370;
          }
          .btn:focus {
            outline: none;
          }
          .btn:hover {
            color: #e62021;
          }
          .btn:hover:after {
            border-bottom-color: #e62021;
          }
          .btn.collapsed:hover:after {
            border-top-color: #e62021;
          }
        `}
      </style>
    </React.Fragment>
  )
}

export default Collapse
